import React, { useState, useEffect } from 'react';
import api from '../../../../api';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import DashboardDropdown from '../../../DashboardComponents/DashboardDropDown';
import CountUp from 'react-countup';
import { getRoundRobinBaseUrl } from '../../../../loadBalancer';
import { FaIndustry, FaClipboardCheck, FaBullseye, FaExclamationTriangle, FaChartLine, FaCalendarCheck } from 'react-icons/fa';
import { useAuth } from '../../../Layouts/context/Authcontext';

const CustomizedAuditPercentage = () => {
  const { accessToken } = useAuth();
  const [auditData, setAuditData] = useState({
    totalProduction: 0,
    totalCompleted: 0,
    auditorTarget: 0,
    errorCount: 0,
    samplingPercentage: 0,
    monthPercentage: 0,
    auditStatus: [
      { name: 'Completed', value: 0, color: '#4CAF50' },
      { name: 'Target', value: 0, color: '#FFC107' },
      { name: 'Errors', value: 0, color: '#F44336' },
    ],
  });

  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedScope, setSelectedScope] = useState(null);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  useEffect(() => {
    const fetchAuditData = async () => {
      if (!accessToken) return;

      let baseUrls = getRoundRobinBaseUrl();
      let url = `${baseUrls}/qaDashboard/getCustomAuditedPercentage`;

      if (selectedClient && selectedScope && fromDate && toDate) {
        url += `?clientId=${selectedClient}&scopeId=${selectedScope}&startDate=${fromDate}&endDate=${toDate}`;
      } else if (selectedClient && selectedScope) {
        url += `?clientId=${selectedClient}&scopeId=${selectedScope}`;
      } else if (selectedClient) {
        url += `?clientId=${selectedClient}`;
      }

      try {
        const response = await api.get(url, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });

        if (response.data.success) {
          const data = response.data.data.customAuditedData;
          setAuditData({
            totalProduction: data.totalProduction || 0,
            totalCompleted: data.totalCompleted || 0,
            auditorTarget: data.auditorTarget || 0,
            errorCount: data.errorCount || 0,
            samplingPercentage: parseFloat(data.samplingPercentage) || 0,
            monthPercentage: data.monthPercentage || 0,
            auditStatus: [
              { name: 'Completed', value: data.totalCompleted || 1, color: '#4CAF50' },
              { name: 'Target', value: data.auditorTarget || 1, color: '#FFC107' },
              { name: 'Errors', value: data.errorCount || 1, color: '#FF0000' },
            ],
          });
        }
      } catch (error) {
        console.error('Error fetching audit data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAuditData();
  }, [accessToken, selectedClient, selectedScope, fromDate, toDate]);

  return (
    <div className="p-6 bg-gradient-to-r from-red-200 via-white-200 to-blue-200">
      <h2 className="text-4xl font-bold text-gray-900 text-center uppercase tracking-wide drop-shadow-lg mb-8">
        Audit Performance Dashboard
      </h2>

      {loading ? (
        <div className="text-center text-lg font-semibold text-gray-600">Loading...</div>
      ) : (
        <>
          <div className="mb-8">
            <DashboardDropdown
              selectedClientId={setSelectedClient}
              selectedScopeId={setSelectedScope}
              selectedFromDate={setFromDate}
              selectedtoDate={setToDate}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: 'Total Production',
                value: auditData.totalProduction,
                icon: <FaIndustry className="text-blue-600 text-6xl mb-4" />,
              },
              {
                title: 'Completed Audits',
                value: auditData.totalCompleted,
                icon: <FaClipboardCheck className="text-green-500 text-6xl mb-4" />,
              },
              {
                title: 'Auditor Target',
                value: auditData.auditorTarget,
                icon: <FaBullseye className="text-purple-500 text-6xl mb-4" />,
              },
              {
                title: 'Error Count',
                value: auditData.errorCount,
                icon: <FaExclamationTriangle className="text-red-500 text-6xl mb-4" />,
              },
              {
                title: 'Sampling Percentage',
                value: auditData.samplingPercentage,
                icon: <FaChartLine className="text-yellow-500 text-6xl mb-4" />,
                suffix: '%',
              },
              {
                title: 'Month Percentage',
                value: auditData.monthPercentage,
                icon: <FaCalendarCheck className="text-orange-500 text-6xl mb-4" />,
                suffix: '%',
              },
            ].map((card, index) => (
              <div
                key={index}
                className="p-8 bg-white bg-opacity-90 border border-gray-200 rounded-3xl shadow-xl flex flex-col items-center hover:shadow-2xl transition-all duration-300"
              >
                {card.icon}
                <h3 className="text-xl font-semibold text-gray-800">{card.title}</h3>
                <p className="text-4xl font-bold text-gray-900 mt-2">
                  <CountUp
                    start={0}
                    end={Number(card.value) || 0}
                    duration={2}
                    separator=","
                    suffix={card.suffix || ''}
                    enableScrollSpy={false}
                    enableOnMount={true}
                  >
                    {({ countUpRef }) => <span ref={countUpRef} />}
                  </CountUp>
                </p>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default CustomizedAuditPercentage;
